import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { FxRate, FxRateSource, FxRefreshResult, ListResponse } from './types'

const ROOT = '/fx-rates'
const KEY = ['fx-rates'] as const

export type FxRatesFilters = {
  currency?: 'USD' | 'EUR' | null
  dateFrom?: string | null
  dateTo?: string | null
  source?: FxRateSource | null
}

function filtersKey(filters: FxRatesFilters) {
  return {
    currency: filters.currency ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    source: filters.source ?? null,
  }
}

export function useFxRates(filters: FxRatesFilters = {}) {
  const api = useApi()
  const key = filtersKey(filters)
  return useQuery({
    queryKey: [...KEY, key],
    queryFn: () =>
      api<ListResponse<FxRate>>(
        `${ROOT}${buildQuery({
          currency: filters.currency ?? undefined,
          date_from: filters.dateFrom ?? undefined,
          date_to: filters.dateTo ?? undefined,
          source: filters.source ?? undefined,
        })}`,
      ),
  })
}

export function flattenFxRates(data: ListResponse<FxRate> | undefined): FxRate[] {
  return data?.items ?? []
}

export function useLatestFxRate(
  currency: 'USD' | 'EUR',
  date?: string,
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'latest', currency, date ?? null],
    enabled: options.enabled ?? true,
    queryFn: () =>
      api<FxRate>(`${ROOT}/latest${buildQuery({ currency, date: date ?? undefined })}`),
  })
}

export type CreateFxRateInput = {
  date: string
  currency: 'USD' | 'EUR'
  buyRate: number
  sellRate: number
}

export function useCreateFxRate() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFxRateInput) =>
      api<FxRate>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type DeleteFxRateInput = {
  date: string
  currency: 'USD' | 'EUR'
  source: FxRateSource
}

export function useDeleteFxRate() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ date, currency, source }: DeleteFxRateInput) =>
      api<void>(`${ROOT}/${date}/${currency}/${source}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useRefreshFxRates() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<FxRefreshResult>(`${ROOT}/refresh`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['reports', 'cashflow'] })
      qc.invalidateQueries({ queryKey: ['transactions'] })
      qc.invalidateQueries({ queryKey: ['account-balance'] })
      qc.invalidateQueries({ queryKey: ['account-movement'] })
    },
  })
}
